import { ConcentrationCalculator } from './ConcentrationCalculator';
import {
  calculateBeta,
  calculateAlpha,
  calculateCompartment,
} from '../utils/calculations_2compartment';

const DEBUG = import.meta.env.VITE_DEBUG ? parseInt(import.meta.env.VITE_DEBUG) : 0;

export class TwoCompartmentConcentrationCalculator extends ConcentrationCalculator {
  private readonly vc: number;
  private readonly alpha: number;
  private readonly beta: number;
  private readonly A: number;
  private readonly B: number;
  private doseGenerator:
    | (() => Generator<{ startHr: any; dose: any; infusion: any; interval: number }, void, void>)
    | null = null;

  constructor(
    vc: number,
    alpha: number,
    beta: number,
    A: number,
    B: number,
    doseGenerator:
      | (() => Generator<{ startHr: any; dose: any; infusion: any; interval: number }, void, void>)
      | null,
  ) {
    super();
    this.vc = vc;
    this.alpha = alpha;
    this.beta = beta;
    this.A = A;
    this.B = B;
    this.doseGenerator = doseGenerator;
  }

  concentrationAtTime(timeDelta: number): number {
    if (this.doseGenerator == null) {
      console.error('No dose generator');
      return 0;
    }
    let concentration = 0.0;
    const generator = this.doseGenerator();
    let doseInfo = generator.next();
    while (doseInfo.value && doseInfo.value?.startHr < timeDelta) {
      const Td = doseInfo.value.startHr;
      const Tinf = doseInfo.value.infusion;
      const dose = doseInfo.value.dose;

      const contribution =
        timeDelta - Td <= Tinf
          ? (dose / Tinf) *
            ((this.A / this.alpha) * (1 - Math.exp(-this.alpha * (timeDelta - Td))) +
              (this.B / this.beta) * (1 - Math.exp(-this.beta * (timeDelta - Td))))
          : (dose / Tinf) *
            ((this.A / this.alpha) *
              ((1 - Math.exp(-this.alpha * Tinf)) *
                Math.exp(-this.alpha * (timeDelta - Td - Tinf))) +
              (this.B / this.beta) *
                ((1 - Math.exp(-this.beta * Tinf)) *
                  Math.exp(-this.beta * (timeDelta - Td - Tinf))));
      concentration += contribution;

      if (DEBUG)
        console.log(
          `Adding contribution from dose(timeDelta=${timeDelta}, Td=${Td}, Tinf=${Tinf}, dose=${dose}) - contribution=${contribution}`,
        );

      doseInfo = generator.next();
    }
    return concentration;
  }

  concentrationDuringInfusion(
    startConcentration: number,
    dosePerHr: number,
    infusionInHrs: number,
    timeDelta: number,
  ): number {
    const result = this.concentrationAtTime(timeDelta);
    if (DEBUG)
      console.log(
        `concentrationDuringInfusion(startConcentration=${startConcentration}, dosePerHr=${dosePerHr}, infusionInHrs=${infusionInHrs}, timeDelta=${timeDelta}) = ${result}`,
      );
    return result;
  }

  concentrationDuringDecay(
    startConcentration: number,
    decayInHrs: number,
    timeDelta: number,
  ): number {
    const result = this.concentrationAtTime(timeDelta);
    if (DEBUG)
      console.log(
        `concentrationDuringDecay(startConcentration=${startConcentration}, decayInHrs=${decayInHrs}, timeDelta=${timeDelta}) result = ${result}`,
      );
    return result;
  }

  calculatePeak(
    intervalInHrs: number,
    infusionInHrs: number,
    dosePerHr: number,
    startTimeDelta = 0,
  ): number {
    // Iterate to steady state, then return the end-of-infusion concentration.
    // startTimeDelta anchors the timeDelta origin to match the doseGenerator (first administered dose).
    const epsilon = 0.00001;
    let timeDelta = startTimeDelta + infusionInHrs;
    let concentration = this.concentrationDuringInfusion(0, dosePerHr, infusionInHrs, timeDelta);
    let lastConcentration = -100;
    const decayInHrs = intervalInHrs - infusionInHrs;
    let iterationCount = 0;
    while (iterationCount < 200 && Math.abs(concentration - lastConcentration) > epsilon) {
      lastConcentration = concentration;
      timeDelta += decayInHrs;
      concentration = this.concentrationDuringDecay(concentration, decayInHrs, timeDelta);
      timeDelta += infusionInHrs;
      concentration = this.concentrationDuringInfusion(
        lastConcentration,
        dosePerHr,
        infusionInHrs,
        timeDelta,
      );
      iterationCount++;
    }
    return concentration;
  }

  calculateTrough(
    intervalInHrs: number,
    infusionInHrs: number,
    dosePerHr: number,
    startTimeDelta = 0,
  ): number {
    // Iterate to steady state, then return the pre-dose (trough) concentration.
    // startTimeDelta anchors the timeDelta origin to match the doseGenerator (first administered dose).
    const epsilon = 0.00001;
    let timeDelta = startTimeDelta;
    let concentration = 0;
    let lastConcentration = -100;
    const decayInHrs = intervalInHrs - infusionInHrs;
    let iterationCount = 0;
    while (iterationCount < 200 && Math.abs(concentration - lastConcentration) > epsilon) {
      lastConcentration = concentration;
      timeDelta += infusionInHrs;
      concentration = this.concentrationDuringInfusion(
        lastConcentration,
        dosePerHr,
        infusionInHrs,
        timeDelta,
      );
      timeDelta += decayInHrs;
      concentration = this.concentrationDuringDecay(concentration, decayInHrs, timeDelta);
      iterationCount++;
    }
    return concentration;
  }

  calculatTmicFromPeak(
    peak: number,
    intervalInHrs: number,
    infusionInHrs: number,
    mic: number | null | undefined,
  ): number | null {
    const micToUse = mic ?? 1.0;

    // Iterate to steady state first
    const epsilon = 0.00001;
    let timeDelta = 0;
    let concentration = 0;
    let lastConcentration = -100;
    let iterationCount = 0;
    while (iterationCount < 200 && Math.abs(concentration - lastConcentration) > epsilon) {
      lastConcentration = concentration;
      timeDelta += intervalInHrs;
      concentration = this.concentrationAtTime(timeDelta);
      iterationCount++;
    }
    // Count time steps above MIC within one interval
    const step = intervalInHrs / 100.0;
    let grmic = 0;
    iterationCount = 1;
    if (concentration > micToUse) {
      grmic++;
    }
    while (iterationCount < 100) {
      timeDelta += step;
      concentration = this.concentrationAtTime(timeDelta);
      if (concentration > micToUse) {
        grmic++;
      }
      iterationCount++;
    }
    return grmic;
  }

  calculateSteadyState(dose: number, intervalInHrs: number, infusionInHrs: number): number {
    // Use iterative convergence: equivalent to calculateTrough at steady state
    // (same approach as calculateTrough, but dosePerHr derived from dose/infusionInHrs)
    const dosePerHr = dose / infusionInHrs;
    return this.calculateTrough(intervalInHrs, infusionInHrs, dosePerHr);
  }
}

// Re-export helper functions from calculations_2compartment for convenience
export { calculateBeta, calculateAlpha, calculateCompartment };

export function calculateACompartment(
  Q: number,
  vd1: number,
  vd2: number,
  alpha: number,
  beta: number,
): number {
  return calculateCompartment('A', Q, vd1, vd2, alpha, beta) as number;
}

export function calculateBCompartment(
  Q: number,
  vd1: number,
  vd2: number,
  alpha: number,
  beta: number,
): number {
  return calculateCompartment('B', Q, vd1, vd2, alpha, beta) as number;
}
